/**
 * Client helpers for Stripe 7-day trial checkout + lightweight device fingerprint
 * (anti-abuse signal stored on user_preferences.trial_fingerprint).
 */
import { getCurrentUser, getCurrentUserToken } from './firebase';

const BACKEND_URL = (() => {
  try {
    return (
      import.meta.env.VITE_BACKEND_URL ||
      import.meta.env.VITE_API_URL ||
      import.meta.env.NEXT_PUBLIC_BACKEND_URL ||
      'https://pixlie-fastapi-eu-8fb92ba4ec59.herokuapp.com'
    );
  } catch {
    return 'https://pixlie-fastapi-eu-8fb92ba4ec59.herokuapp.com';
  }
})().replace(/\/$/, '');

const FP_KEY = 'pixlie_trial_fp_v1';

export function getOrCreateTrialFingerprint(): string {
  try {
    const existing = localStorage.getItem(FP_KEY);
    if (existing && existing.length >= 8) return existing;
    const seed = [
      navigator.userAgent,
      navigator.language,
      String(screen.width),
      String(screen.height),
      String(screen.colorDepth),
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      String(Date.now()),
      Math.random().toString(36).slice(2),
    ].join('|');
    let h = 2166136261;
    for (let i = 0; i < seed.length; i += 1) {
      h ^= seed.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    const fp = `fp_${(h >>> 0).toString(16)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(FP_KEY, fp);
    return fp;
  } catch {
    return `fp_anon_${Date.now().toString(36)}`;
  }
}

export async function createTrialCheckoutSession(opts?: {
  successUrl?: string;
  cancelUrl?: string;
}): Promise<{ checkout_url: string; session_id: string }> {
  const user = getCurrentUser();
  const token = await getCurrentUserToken();
  if (!user?.email || !token) {
    throw new Error('Sign in to start a trial.');
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://pixlys.com';
  const body = {
    user_email: user.email,
    success_url: opts?.successUrl || `${origin}/billing?trial=success`,
    cancel_url: opts?.cancelUrl || `${origin}/billing?trial=cancel`,
    trial_fingerprint: getOrCreateTrialFingerprint(),
  };
  const response = await fetch(`${BACKEND_URL}/api/billing/create-trial-checkout`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail =
      (typeof data.detail === 'object' && (data.detail?.message || data.detail?.error)) ||
      (typeof data.detail === 'string' && data.detail) ||
      'Could not start trial checkout.';
    throw new Error(String(detail));
  }
  if (!data.checkout_url) {
    throw new Error('Trial checkout URL missing.');
  }
  return { checkout_url: data.checkout_url, session_id: data.session_id };
}

export type OnboardingGateStatus = {
  paywall_required: boolean;
  soft_warning?: boolean;
  free_videos_used: number;
  free_video_limit: number;
  free_videos_remaining?: number;
  trial_days: number;
  daily_video_jobs?: number;
  daily_video_limit?: number;
  daily_videos_remaining?: number;
  monthly_video_jobs?: number;
  monthly_video_limit?: number;
  monthly_videos_remaining?: number;
  plan?: string;
  subscription_status?: string;
};

export async function fetchOnboardingGate(): Promise<OnboardingGateStatus | null> {
  const user = getCurrentUser();
  const token = await getCurrentUserToken();
  if (!user?.uid || !token) return null;
  try {
    const response = await fetch(`${BACKEND_URL}/api/billing/onboarding-gate/${user.uid}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

export async function startTrialCheckoutRedirect(): Promise<void> {
  const { checkout_url } = await createTrialCheckoutSession();
  window.location.assign(checkout_url);
}
